import React, { useCallback, useEffect, useState } from 'react';

import { close, down } from '@/assets/icons';
import {
	ChatContainer,
	ChatContainerSendMessageFn,
} from '@/components/ChatContainer';
import { AsaphIconButton } from '@/components/shared/Button';
import { InviteTeamMemberDialog } from '@/components/shared/InviteTeamMemberDialog';
import {
	INITIAL_MESSAGES_SIZE,
	sendingMessageStatus,
} from '@/config/constants';
import {
	FetchChatReactionsMutation,
	GetVideoQuery,
	MessageType,
	useFetchChatReactionsMutation,
	useFetchVideoChatMessagesMutation,
	useSendVideoChatMessageMutation,
	useUpsertChatStatMutation,
} from '@/graphql';
import { deduplicateMessages } from '@/helpers/deduplicateMessages';
import { useAuth } from '@/providers/AuthProvider';
import { useUnreadChats } from '@/providers/UnreadChatsProvider/UnreadChatsProvider';
import { SvgIcon } from '@material-ui/core';
import { groupBy, last } from 'ramda';
import { v4 as uuidv4 } from 'uuid';

import './ChatVideo.scss';
import { AddTeamMemberButton } from './components/AddTeamMemberButton';

type Messages = NonNullable<
	NonNullable<GetVideoQuery['getVideo']['chat']>['messages']
>['items'];

const byId = groupBy(function (
	reaction: FetchChatReactionsMutation['getChatReactions'][0],
) {
	return reaction.messageId;
});

export interface IChatVideoProps {
	videoId: string;
	refetchVideo: () => Promise<any>;
	chat: GetVideoQuery['getVideo']['chat'];
	closeable: boolean;
	onExpand: () => void;
	onClose: () => void;
	expanded?: boolean;
}

export const ChatVideo: React.FC<IChatVideoProps> = ({
	videoId,
	refetchVideo,
	chat,
	closeable,
	expanded,
	onExpand,
	onClose,
}) => {
	const { user } = useAuth();
	const { setVideoChatIds } = useUnreadChats();
	const [dialogOpen, setDialogOpen] = useState<boolean>(false);
	const openAddTeamMemberDialog = useCallback(() => setDialogOpen(true), []);
	const [offset, setOffset] = useState(INITIAL_MESSAGES_SIZE);
	const [previousMessagesLoading, setPreviousMessagesLoading] =
		useState(false);
	const [hasMoreMessages, setHasMoreMessages] = useState(true);

	const { mutateAsync: fetchVideoChatMessages } =
		useFetchVideoChatMessagesMutation();
	const { mutateAsync: fetchChatReactions } = useFetchChatReactionsMutation();
	const { mutateAsync: upsertChatStat } = useUpsertChatStatMutation();

	useEffect(() => {
		const fetchNewMessages = async () => {
			try {
				const allSentMessages =
					chat?.messages?.items?.filter(
						(m) => m.createdAt !== sendingMessageStatus,
					) || [];

				const firstDate =
					allSentMessages[0]?.createdAt || new Date().toISOString();

				const lastDate =
					last(allSentMessages)?.createdAt ||
					new Date().toISOString();

				const newMessages = await fetchVideoChatMessages({
					videoId,
					input: {
						limit: -1,
						offset: 0,
						afterDate: lastDate,
					},
				});

				const reactions = await fetchChatReactions({
					chatId: newMessages.getVideoChat.id,
					fromDate: firstDate,
				});

				// The BackEnd will return chat.id as 0 if there is no chat
				// for the video yet. In such a case, there is no point of sending
				// a chatStat as it would not affect any queries related to what is "seen"
				// by the user.
				if (newMessages.getVideoChat.id !== '0') {
					upsertChatStat({
						chatId: newMessages.getVideoChat.id,
					});
				}

				const reactionsByMessageId = byId(
					reactions.getChatReactions || [],
				);

				if (chat?.messages?.items) {
					chat.messages.items = deduplicateMessages([
						...chat.messages.items,
						...(newMessages.getVideoChat.messages?.items ?? []),
					]).map((m) => ({
						...m,
						reactions: reactionsByMessageId[m.id],
					}));
					chat.messages.totalCount =
						newMessages.getVideoChat.messages?.totalCount ?? 0;
				} else {
					refetchVideo();
				}
			} catch (e) {
				console.log(e);
			}
		};

		let interval;
		interval = setInterval(() => {
			fetchNewMessages();
		}, 1000);

		return () => clearInterval(interval);
	}, [
		chat,
		fetchChatReactions,
		fetchVideoChatMessages,
		refetchVideo,
		videoId,
		upsertChatStat,
	]);
	const { mutateAsync: sendVideoChatMessage } =
		useSendVideoChatMessageMutation();

	// Mark the chat as "read" by removing it form the
	// global unread chats resource
	// The BackEnd will return chat.id as 0 if there is no chat
	// for the video yet. In such a case, there is no point of sending
	// a chatStat as it would not affect any queries related to what is "seen"
	// by the user.
	useEffect(() => {
		if (chat?.id && chat.id !== '0') {
			console.log('REMOVING ', chat.id, 'FROM VIDEO CHAT IDS');
			setVideoChatIds((chatIds) =>
				chatIds.filter((id) => id !== chat.id),
			);
			upsertChatStat({
				chatId: chat.id,
			});
		}
	}, [setVideoChatIds, upsertChatStat, chat]);

	const sendMessage = useCallback<ChatContainerSendMessageFn>(
		(value, type) => {
			const id = uuidv4();
			if (chat?.messages?.items) {
				chat.messages.items = [
					...chat.messages.items,
					{
						type: type ?? MessageType.Text,
						content: value,
						createdAt: sendingMessageStatus,
						id,
						userId: user.id,
						user: {
							displayName: user.displayName,
							id: user.id,
						},
						reactions: [],
					},
				];
			}
			return sendVideoChatMessage({
				videoId,
				input: { id, content: value },
			});
		},
		[
			chat?.messages,
			sendVideoChatMessage,
			user.displayName,
			user.id,
			videoId,
		],
	);

	const fetchPreviousMessages = useCallback(async () => {
		setPreviousMessagesLoading(true);
		const previousMessagesQuery = await fetchVideoChatMessages({
			videoId,
			input: {
				limit: INITIAL_MESSAGES_SIZE,
				offset,
			},
		});

		const previousMessages =
			previousMessagesQuery.getVideoChat.messages?.items ?? [];
		if (chat?.messages?.items) {
			chat.messages.items = deduplicateMessages([
				...previousMessages,
				...chat.messages.items,
			]);
		}
		if (previousMessages.length < INITIAL_MESSAGES_SIZE) {
			setHasMoreMessages(false);
		} else {
			setHasMoreMessages(true);
		}
		setOffset((prevOffset) => prevOffset + INITIAL_MESSAGES_SIZE);
		setPreviousMessagesLoading(false);
	}, [chat?.messages, offset, videoId, fetchVideoChatMessages]);

	const classes = [
		'videoChat',
		!expanded && closeable ? 'videoChat--with-rounded-borders' : '',
	];

	return (
		<section className={classes.join(' ')}>
			<div className="videoChat__header-container">
				<div className="videoChat__header">
					<div className="videoChat__header-left">
						<AddTeamMemberButton
							onClick={openAddTeamMemberDialog}
						/>
					</div>
					{closeable && (
						<div className="videoChat__header-right">
							<AsaphIconButton
								icon={
									<SvgIcon
										component={down}
										classes={{
											root: expanded
												? ''
												: 'videoChat__header-icon--expanded',
										}}
									/>
								}
								size="small"
								color="grey"
								aria-label="expand chat"
								onClick={onExpand}
							/>
							<AsaphIconButton
								icon={<SvgIcon component={close} />}
								size="small"
								color="grey"
								aria-label="close chat"
								onClick={onClose}
							/>
						</div>
					)}
				</div>
			</div>
			<ChatContainer
				className="videoChat_chat-container"
				chat={chat}
				sendMessage={sendMessage}
				fetchPreviousMessages={fetchPreviousMessages}
				previousMessagesLoading={previousMessagesLoading}
				hasMoreMessages={hasMoreMessages}
				userStats={chat?.userStats}
			/>
			<InviteTeamMemberDialog
				open={dialogOpen}
				onClose={() => setDialogOpen(false)}
			/>
		</section>
	);
};
